import PropTypes from 'prop-types';

/**
 * Standard shape and helpers for files we upload to Google Cloud Storage
 * (or in the future, a CDN in front of it). Used for creator/org profile
 * images, live-room behind-the-scenes videos, post images, conversation
 * attachments, etc. — anything that doesn't go through Sharetribe's
 * own image pipeline.
 *
 * Shape (returned by the /api/google-storage/* endpoints):
 *   {
 *     id:        string  // short UUID generated server-side
 *     name:      string  // "<id>-<originalFilename>"
 *     link:      string  // raw GCS URL
 *     createdAt: string  // ISO timestamp
 *     kind:      'image' | 'video' | 'document'   // optional, set client-side
 *   }
 */

export const MEDIA_KIND = Object.freeze({
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document',
});

export const uploadedMediaShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  name: PropTypes.string,
  createdAt: PropTypes.string,
  kind: PropTypes.oneOf(Object.values(MEDIA_KIND)),
});

/**
 * Type guard. True for objects that look like an uploaded-media record.
 * Useful when an avatar field could hold either a Sharetribe image entity
 * or a raw GCS upload result.
 */
export const isUploadedMedia = value =>
  !!value &&
  typeof value === 'object' &&
  typeof value.id === 'string' &&
  typeof value.link === 'string';

/**
 * Resolve a usable URL for a stored media record.
 *
 * `sizeHint` is informational only today — every consumer gets the raw
 * `link`. When we put a CDN (Cloudflare Images / imgix) in front, the
 * implementation here changes to attach width/height/fit query params
 * based on the hint. The call sites stay identical.
 *
 * Common hints:
 *   'thumbnail' (~80px), 'avatar' (~200px), 'card' (~600px), 'hero' (~1440px)
 *
 * Pass a custom { width, height, fit } object for ad-hoc sizes.
 */
export const getMediaUrl = (media, _sizeHint) => {
  if (!isUploadedMedia(media)) return null;
  return media.link;
};

/**
 * Normalize whatever the upload endpoint returned + the source File
 * into the canonical shape. Use this in upload thunks so consumers
 * always see the same fields.
 */
export const normalizeUploadResponse = (response, kind, file) => {
  if (!response) return null;
  return {
    id: response.id,
    link: response.link,
    name: response.name || file?.name || null,
    createdAt: response.createdAt || new Date().toISOString(),
    kind,
  };
};
